import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Database,
  Eye,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FOREST_GREEN } from "@/styles/tokens";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import * as errorUtils from "@/utils/errors";
import {
  createComponentDataTemplate,
  listComponentDataViewCandidates,
  listComponentSchemaSourceCandidates,
} from "@/services/api/componentSchemaBindingApi";
import {
  COMPONENT_DATA_TEMPLATE_MAX_SOURCES,
  isSelectableCatalogSourceCandidate,
  parseDataSourceRef,
  parseDataViewRef,
  type CatalogDataViewCandidate,
  type CatalogSourceCandidate,
  type ComponentDataTemplateCreateResponse,
  type DataCatalogSelection,
  type DataSourceRef,
  type DataViewRef,
} from "@/types/componentDataBinding";
import { cn } from "@/lib/utils";

export type DataModalSelection = DataCatalogSelection;

export interface DataModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect?: (selection: DataModalSelection) => void;
  onTemplateCreated?: (template: ComponentDataTemplateCreateResponse) => void;
}

const CURRENT_ORG_REQUIRED_MESSAGE = "Select an organization to browse data.";

type DataModalTab = "sources" | "views";

interface DataModalSelectedSourceItem {
  source: DataSourceRef;
  label: string;
  labels: CatalogSourceCandidate["labels"];
  capabilities: CatalogSourceCandidate["capabilities"];
}

type DataModalSelectedItem =
  | {
      kind: "sources";
      sources: DataModalSelectedSourceItem[];
    }
  | {
      kind: "view";
      view: DataViewRef;
      label: string;
    };

function isDataTab(value: string): value is DataModalTab {
  return value === "sources" || value === "views";
}

function getSourceRefKey(source: DataSourceRef): string {
  return [
    source.dataOwnerOrganizationId,
    source.ownerType,
    source.ownerId,
    source.groupId,
    source.schemaId,
  ].join(":");
}

function getSourceCandidateKey(candidate: CatalogSourceCandidate): string {
  return getSourceRefKey(candidate.source);
}

function getViewCandidateKey(candidate: CatalogDataViewCandidate): string {
  const { view } = candidate;
  return [
    view.kind,
    view.viewId,
    view.ownerType,
    view.ownerId,
    view.viewOrganizationId,
  ].join(":");
}

function getSourceLabel(candidate: CatalogSourceCandidate): string {
  return candidate.labels.schemaDisplayName || candidate.labels.schemaName;
}

function getSelectedSourceItem(
  candidate: CatalogSourceCandidate,
): DataModalSelectedSourceItem | null {
  if (!isSelectableCatalogSourceCandidate(candidate)) {
    return null;
  }

  const source = parseDataSourceRef(candidate.source);
  if (source === undefined) {
    return null;
  }

  return {
    source,
    label: getSourceLabel(candidate),
    labels: candidate.labels,
    capabilities: candidate.capabilities,
  };
}

function getViewKindLabel(view: DataViewRef): string {
  if (view.kind === "query-template") {
    return "Query template compatibility";
  }
  if (view.kind === "saved-view") {
    return "Saved view";
  }
  return "Federated view";
}

function getOwnerScopeLabel(ownerType: DataSourceRef["ownerType"]): string {
  if (ownerType === "organization") {
    return "Organization";
  }
  if (ownerType === "project") {
    return "Project";
  }
  return "User";
}

function getSourceSearchText(candidate: CatalogSourceCandidate): string {
  return [
    getSourceLabel(candidate),
    candidate.labels.schemaName,
    candidate.labels.groupName,
    candidate.labels.groupSlug,
    candidate.source.ownerType,
    candidate.source.ownerId,
    candidate.source.dataOwnerOrganizationId,
  ].join(" ");
}

function getViewSearchText(candidate: CatalogDataViewCandidate): string {
  return [
    candidate.labels.name,
    candidate.labels.description,
    candidate.labels.ownerName,
    candidate.labels.viewOrganizationName,
    candidate.view.kind,
    candidate.view.ownerType,
    candidate.view.ownerId,
    candidate.view.viewOrganizationId,
  ].join(" ");
}

export const DataModal: React.FC<DataModalProps> = ({
  open,
  onOpenChange,
  onSelect,
  onTemplateCreated,
}) => {
  const requestingOrganizationId = useOrganizationStore((state) =>
    state.currentContext?.type === "organization"
      ? state.currentContext.id
      : null,
  );
  const [activeTab, setActiveTab] = useState<DataModalTab>("sources");
  const [sourceCandidates, setSourceCandidates] = useState<
    CatalogSourceCandidate[]
  >([]);
  const [viewCandidates, setViewCandidates] = useState<
    CatalogDataViewCandidate[]
  >([]);
  const [selection, setSelection] = useState<DataModalSelectedItem | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateCreateMessage, setTemplateCreateMessage] = useState<
    string | null
  >(null);
  const [searchInput, setSearchInput] = useState("");

  const fetchData = useCallback(async () => {
    if (requestingOrganizationId === null) {
      setSourceCandidates([]);
      setViewCandidates([]);
      setSelection(null);
      setLoading(false);
      setError(CURRENT_ORG_REQUIRED_MESSAGE);
      setTemplateCreateMessage(null);
      return;
    }

    setLoading(true);
    setError(null);
    setTemplateCreateMessage(null);
    try {
      const [sourcesResult, viewsResult] = await Promise.all([
        listComponentSchemaSourceCandidates({
          requestingOrganizationId,
          operation: "read",
        }),
        listComponentDataViewCandidates({
          requestingOrganizationId,
          kinds: ["query-template", "federated-view"],
        }),
      ]);

      setSourceCandidates(
        sourcesResult.candidates.filter(isSelectableCatalogSourceCandidate),
      );
      setViewCandidates(
        viewsResult.candidates.filter((candidate) => {
          const view = parseDataViewRef(candidate.view);
          return (
            view !== undefined &&
            view.kind === candidate.viewKind &&
            (candidate.viewKind === "query-template" ||
              candidate.viewKind === "federated-view")
          );
        }),
      );
    } catch (err: unknown) {
      setSourceCandidates([]);
      setViewCandidates([]);
      setError(
        errorUtils.getErrorMessageOrFallback(
          err,
          "Failed to load data catalog",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [requestingOrganizationId]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  useEffect(() => {
    if (requestingOrganizationId === null) {
      setSourceCandidates([]);
      setViewCandidates([]);
      setSelection(null);
      setLoading(false);
      setError(open ? CURRENT_ORG_REQUIRED_MESSAGE : null);
      setTemplateCreateMessage(null);
    }
  }, [open, requestingOrganizationId]);

  useEffect(() => {
    if (!open) {
      setActiveTab("sources");
      setSearchInput("");
      setTemplateCreateMessage(null);
    }
  }, [open]);

  const filteredSources = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return sourceCandidates;
    return sourceCandidates.filter((candidate) =>
      getSourceSearchText(candidate).toLowerCase().includes(q),
    );
  }, [searchInput, sourceCandidates]);

  const filteredViews = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return viewCandidates;
    return viewCandidates.filter((candidate) =>
      getViewSearchText(candidate).toLowerCase().includes(q),
    );
  }, [searchInput, viewCandidates]);

  const selectedSourceKeys = useMemo(() => {
    if (selection?.kind !== "sources") {
      return new Set<string>();
    }
    return new Set(
      selection.sources.map((item) => getSourceRefKey(item.source)),
    );
  }, [selection]);

  const selectedViewKey = useMemo(() => {
    if (selection?.kind !== "view") {
      return null;
    }
    return [
      selection.view.kind,
      selection.view.viewId,
      selection.view.ownerType,
      selection.view.ownerId,
      selection.view.viewOrganizationId,
    ].join(":");
  }, [selection]);

  const handleSelectSource = useCallback(
    (candidate: CatalogSourceCandidate) => {
      const selectedItem = getSelectedSourceItem(candidate);
      if (selectedItem === null) {
        setError("Source is not backed by supported catalog authority.");
        return;
      }
      const selectedKey = getSourceRefKey(selectedItem.source);

      if (
        selection?.kind === "sources" &&
        !selection.sources.some((item) => getSourceRefKey(item.source) === selectedKey) &&
        selection.sources.length >= COMPONENT_DATA_TEMPLATE_MAX_SOURCES
      ) {
        setError("Select at most 8 sources for one template.");
        setTemplateCreateMessage(null);
        return;
      }

      setError(null);
      setTemplateCreateMessage(null);
      setSelection((currentSelection) => {
        if (currentSelection?.kind !== "sources") {
          return { kind: "sources", sources: [selectedItem] };
        }

        const alreadySelected = currentSelection.sources.some(
          (item) => getSourceRefKey(item.source) === selectedKey,
        );
        if (alreadySelected) {
          const sources = currentSelection.sources.filter(
            (item) => getSourceRefKey(item.source) !== selectedKey,
          );
          return sources.length === 0 ? null : { kind: "sources", sources };
        }

        return {
          kind: "sources",
          sources: [...currentSelection.sources, selectedItem],
        };
      });
      onSelect?.({
        selectionType: "source",
        source: selectedItem.source,
        labels: candidate.labels,
        capabilities: candidate.capabilities,
        ...(candidate.capabilities.writable
          ? { writeTarget: selectedItem.source }
          : {}),
      });
    },
    [onSelect, selection],
  );

  const handleSelectView = useCallback(
    (candidate: CatalogDataViewCandidate) => {
      const view = parseDataViewRef(candidate.view);
      if (view === undefined || view.kind !== candidate.viewKind) {
        setError("View is missing executable catalog identity.");
        return;
      }

      setError(null);
      setTemplateCreateMessage(null);
      setSelection({
        kind: "view",
        view,
        label: candidate.labels.name,
      });
      onSelect?.({
        selectionType: "view",
        view,
        labels: candidate.labels,
      });
    },
    [onSelect],
  );

  const handleCreateTemplate = useCallback(async () => {
    if (requestingOrganizationId === null) {
      setError(
        "Organization context is required to create an executable template.",
      );
      setTemplateCreateMessage(null);
      return;
    }

    if (selection === null) {
      setError(
        "Select a source or query-template view before creating a template.",
      );
      setTemplateCreateMessage(null);
      return;
    }

    setCreatingTemplate(true);
    setError(null);
    setTemplateCreateMessage(null);

    try {
      const template = await (async () => {
        if (selection.kind === "sources") {
          const firstSource = selection.sources[0];
          if (firstSource === undefined) {
            return null;
          }

          if (selection.sources.length === 1) {
            return createComponentDataTemplate({
              templateSource: "catalog-source",
              requestingOrganizationId,
              source: firstSource.source,
              name: firstSource.label,
            });
          }

          return createComponentDataTemplate({
            templateSource: "catalog-sources",
            requestingOrganizationId,
            sources: selection.sources.map((item) => item.source),
            name: selection.sources.map((item) => item.label).join(" + "),
          });
        }

        if (selection.view.kind === "query-template") {
          return createComponentDataTemplate({
            templateSource: "query-template-view",
            requestingOrganizationId,
            view: selection.view,
            name: selection.label,
          });
        }

        if (selection.view.kind === "federated-view") {
          return createComponentDataTemplate({
            templateSource: "federated-view",
            requestingOrganizationId,
            view: selection.view,
            name: selection.label,
          });
        }

        return null;
      })();

      if (template === null) {
        setError(
          "Only query-template-compatible or federated views can create templates.",
        );
        return;
      }

      if (!template.executable) {
        setTemplateCreateMessage(
          template.reviewReason
            ? `Needs Review: ${template.name}. ${template.reviewReason}`
            : `Needs Review: ${template.name}.`,
        );
        return;
      }

      setTemplateCreateMessage(`Created executable template ${template.name}.`);
      onTemplateCreated?.(template);
    } catch (err: unknown) {
      setError(
        errorUtils.getErrorMessageOrFallback(
          err,
          "Failed to create executable template",
        ),
      );
    } finally {
      setCreatingTemplate(false);
    }
  }, [onTemplateCreated, requestingOrganizationId, selection]);

  const canCreateTemplate =
    selection !== null &&
    requestingOrganizationId !== null &&
    !creatingTemplate &&
    (selection.kind === "sources"
      ? selection.sources.length > 0 &&
        selection.sources.length <= COMPONENT_DATA_TEMPLATE_MAX_SOURCES
      : selection.view.kind === "query-template" ||
        selection.view.kind === "federated-view");

  const totalEntries = sourceCandidates.length + viewCandidates.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="p-1.5 rounded-lg bg-primary/10">
            <Database
              className="h-5 w-5"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Data
              {totalEntries > 0 && (
                <span className="text-xs font-normal text-muted-foreground ml-1.5">
                  ({sourceCandidates.length} sources, {viewCandidates.length}{" "}
                  views)
                </span>
              )}
            </h2>
            <p className="text-[11px] text-muted-foreground">
              Select catalog sources or scoped views
            </p>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            if (isDataTab(value)) {
              setActiveTab(value);
            }
          }}
        >
          <div className="border-b border-border px-4 pb-3">
            <TabsList className="w-full">
              <TabsTrigger value="sources" className="flex-1 text-xs">
                Sources
              </TabsTrigger>
              <TabsTrigger value="views" className="flex-1 text-xs">
                Views
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="border-b border-border px-4 py-3 flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search data catalog..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              {searchInput && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {selection !== null && (
            <div className="border-b border-border px-4 py-2 text-xs">
              <div className="flex flex-col gap-2 rounded-md bg-muted px-3 py-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex min-w-0 items-start gap-2">
                  <Eye className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    {selection.kind === "sources" ? (
                      <>
                        <p className="font-medium text-foreground">
                          Selected{" "}
                          {selection.sources.length === 1
                            ? "source"
                            : "sources"}
                          :{" "}
                          {selection.sources
                            .map((item) => item.label)
                            .join(", ")}
                        </p>
                        {selection.sources.map((item) => (
                          <p
                            key={getSourceRefKey(item.source)}
                            className="mt-0.5 text-muted-foreground"
                          >
                            {getOwnerScopeLabel(item.source.ownerType)} owner{" "}
                            {item.source.ownerId} | data org{" "}
                            {item.source.dataOwnerOrganizationId} | group{" "}
                            {item.source.groupId} | schema{" "}
                            {item.source.schemaId}
                          </p>
                        ))}
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-foreground">
                          Selected view: {selection.label}
                        </p>
                        <p className="mt-0.5 text-muted-foreground">
                          {getViewKindLabel(selection.view)} |{" "}
                          {getOwnerScopeLabel(selection.view.ownerType)} owner{" "}
                          {selection.view.ownerId} | view org{" "}
                          {selection.view.viewOrganizationId} | view{" "}
                          {selection.view.viewId}
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateTemplate}
                  disabled={!canCreateTemplate}
                  className="shrink-0 gap-1.5 text-xs"
                >
                  {creatingTemplate ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5" />
                  )}
                  Create template
                </Button>
              </div>
            </div>
          )}

          {templateCreateMessage && (
            <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-primary/10 text-primary text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {templateCreateMessage}
            </div>
          )}

          {error && (
            <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <TabsContent
            value="sources"
            className="data-[state=active]:h-[60vh] data-[state=inactive]:h-0 overflow-y-auto mt-0"
          >
            <div className="border-b border-border px-4 py-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                Catalog sources with explicit data-owner identity
              </span>
            </div>

            {loading && sourceCandidates.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSources.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-muted-foreground py-16 px-8">
                <Database className="w-12 h-12 mb-3 opacity-40" />
                <h3 className="text-base font-medium text-foreground mb-1">
                  No Sources Found
                </h3>
                <p className="text-sm text-center max-w-sm">
                  {searchInput
                    ? "No sources match your search."
                    : "No executable catalog sources are available."}
                </p>
              </div>
            ) : (
              <div className="p-4 grid gap-3 grid-cols-1">
                {filteredSources.map((candidate) => {
                  const key = getSourceCandidateKey(candidate);
                  const selected = selectedSourceKeys.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handleSelectSource(candidate)}
                      className={cn(
                        "border rounded-lg p-3 text-left transition-colors",
                        selected
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-border/80",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-md bg-primary/10 mt-0.5">
                          <Database
                            className="h-4 w-4"
                            style={{ color: FOREST_GREEN.DEFAULT }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {getSourceLabel(candidate)}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {getOwnerScopeLabel(candidate.source.ownerType)}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {candidate.capabilities.writable
                                ? "Read/write"
                                : "Read"}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {candidate.labels.groupName} |{" "}
                            {candidate.labels.groupSlug}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">
                            data org {candidate.source.dataOwnerOrganizationId}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                            owner {candidate.source.ownerId} | group{" "}
                            {candidate.source.groupId} | schema{" "}
                            {candidate.source.schemaId}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent
            value="views"
            className="data-[state=active]:h-[60vh] data-[state=inactive]:h-0 overflow-y-auto mt-0"
          >
            <div className="p-4">
              {loading && viewCandidates.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredViews.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-muted-foreground py-16">
                  <Database className="w-12 h-12 mb-3 opacity-40" />
                  <p className="text-sm">
                    {searchInput
                      ? "No views match your search."
                      : "No scoped views found."}
                  </p>
                </div>
              ) : (
                <div className="grid gap-3 grid-cols-1">
                  {filteredViews.map((candidate) => {
                    const key = getViewCandidateKey(candidate);
                    const selected = selectedViewKey === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleSelectView(candidate)}
                        className={cn(
                          "border rounded-lg p-3 text-left transition-colors",
                          selected
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-border/80",
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground truncate">
                                {candidate.labels.name}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                {getViewKindLabel(candidate.view)}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                {getOwnerScopeLabel(candidate.view.ownerType)}
                              </span>
                            </div>
                            {candidate.labels.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {candidate.labels.description}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {candidate.labels.ownerName ||
                                `Owner ${candidate.view.ownerId}`}{" "}
                              |{" "}
                              {candidate.labels.viewOrganizationName ||
                                `View org ${candidate.view.viewOrganizationId}`}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                              view org {candidate.view.viewOrganizationId} |
                              owner {candidate.view.ownerId}
                            </p>
                          </div>
                          <span className="shrink-0 text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary">
                            {candidate.view.viewId}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
